/**
 * TeacherMyClasses.jsx
 * ────────────────────
 * Teacher's "My Classes" view — rectangle cards grouped by Department → Batch.
 * Click a class → opens a big, full-screen Deskey-themed modal with:
 *   • Chapters (lessons) sidebar + create-chapter
 *   • Topics timeline per chapter (daily publishes with preview)
 *   • Info / Students / Pending / Add Student tabs
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Users, Mail, CheckCircle2, Search, Plus, Trash2, ChevronRight, GraduationCap,
  BookOpen, FileText, Calendar, Sparkles, Layers, Clock, Eye, Pencil, Activity,
  ClipboardList, CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClassInsightsPanel from '@/components/teacher/ClassInsightsPanel';
import SessionPreview from '@/components/teacher/ContentEditor/SessionPreview';
import AssignmentBlock from '@/components/AssignmentBlock';
import { notifyError } from '../lib/errors';

const API = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token') || '';
  const res = await fetch(`${API}/api${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  const txt = await res.text();
  try {
    return JSON.parse(txt);
  } catch {
    return { detail: txt };
  }
};

// ── Class Card ──────────────────────────────────────────────────────────────
function ClassCard({ cls, onOpen, onQuickPick }) {
  const subjectLine = (cls.subjects || []).join(' · ') || 'Subject';
  return (
    <div
      className="group rounded-2xl bg-white border border-zinc-200 hover:border-[#34A853]/50 hover:shadow-lg transition-all cursor-pointer overflow-hidden relative"
      style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
      data-testid={`class-card-${cls.id}`}
      onClick={() => onOpen(cls)}
    >
      <div
        className="h-1.5 w-full"
        style={{ background: 'linear-gradient(90deg, #34A853, #06B6D4, #8B5CF6)' }}
      />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#34A853] mb-0.5 truncate">
              {subjectLine}
            </p>
            <h4 className="text-[15px] font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {cls.name}
            </h4>
            {cls.class_code && (
              <p className="text-[10px] font-mono text-gray-400 mt-0.5">#{cls.class_code}</p>
            )}
          </div>
          {cls.pending_count > 0 && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0">
              {cls.pending_count} pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-3">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{cls.student_count}/{cls.capacity || '∞'}</span>
          {(cls.lesson_count > 0) && (
            <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{cls.lesson_count}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onQuickPick && (
            <button
              onClick={(e) => { e.stopPropagation(); onQuickPick(cls); }}
              className="flex-1 text-[11px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] px-3 py-2 rounded-lg transition-colors"
              data-testid={`class-pick-${cls.id}`}
            >
              Select
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onOpen(cls); }}
            className={`${onQuickPick ? '' : 'flex-1'} text-[11px] font-semibold text-gray-600 hover:text-[#34A853] hover:bg-[#34A853]/5 border border-gray-200 hover:border-[#34A853]/30 px-3 py-2 rounded-lg transition-all flex items-center gap-1 justify-center`}
            data-testid={`class-details-${cls.id}`}
          >
            More details <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Grouped Class Grid ──────────────────────────────────────────────────────
export function GroupedClassGrid({ groups, onOpenClass, onQuickPick, emptyMessage }) {
  if (!groups || groups.length === 0) {
    return (
      <div className="p-10 text-center bg-white rounded-2xl border border-dashed border-zinc-200">
        <GraduationCap className="w-10 h-10 mx-auto text-gray-300 mb-2" />
        <p className="text-[13px] text-gray-500">{emptyMessage || 'No classes assigned yet.'}</p>
      </div>
    );
  }
  return (
    <div className="space-y-7" data-testid="grouped-class-grid">
      {groups.map((g, gi) => (
        <section key={g.department.id + gi} data-testid={`dept-group-${g.department.id}`}>
          <div className="flex items-center gap-3 mb-3 pb-2 border-b border-zinc-100">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: g.department.sticker_color || '#34A853' }}
            >
              <GraduationCap className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[14px] font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {g.department.name}
              </h3>
              {g.department.code && <p className="text-[10px] font-mono text-gray-400">{g.department.code}</p>}
            </div>
          </div>
          <div className="space-y-5">
            {g.batches.map((b, bi) => (
              <div key={b.batch.id + bi} data-testid={`batch-group-${b.batch.id}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">{b.batch.name}</span>
                  {b.batch.year && <span className="text-[10px] text-gray-400">· {b.batch.year}</span>}
                  <span className="text-[10px] text-gray-400 ml-auto">{b.classes.length} class{b.classes.length !== 1 ? 'es' : ''}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {b.classes.map((c) => (
                    <ClassCard key={c.id} cls={c} onOpen={onOpenClass} onQuickPick={onQuickPick} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  Class Detail Modal — BIG full-screen Deskey-themed popup
//  Exposes the same public API: <ClassDetailDrawer classId onClose onStudentsChanged />
// ══════════════════════════════════════════════════════════════════════════
export function ClassDetailDrawer({ classId, onClose, onStudentsChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [students, setStudents] = useState([]);
  const [pending, setPending] = useState([]);
  const [available, setAvailable] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('lessons'); // lessons | info | students | pending | assignments | add
  const [busy, setBusy] = useState(false);

  // New-assignment form (Assignments tab)
  const [showNewAssignment, setShowNewAssignment] = useState(false);
  const [newAssignmentChapterId, setNewAssignmentChapterId] = useState('');
  const [newAssignmentTopicId, setNewAssignmentTopicId] = useState('');
  const [newAssignment, setNewAssignment] = useState({
    title: '', description: '', dueDate: '', dueTime: '23:59', points: 10,
  });

  // Chapter/Topic navigation state
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [topicsByChapter, setTopicsByChapter] = useState({}); // { [chapter_id]: topics[] }
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [viewingTopic, setViewingTopic] = useState(null);
  const [chapterSearch, setChapterSearch] = useState('');
  const [topicSearch, setTopicSearch] = useState('');

  // Chapter edit inline form
  const [editingChapterId, setEditingChapterId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');

  // New-chapter form
  const [showNewChapter, setShowNewChapter] = useState(false);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterDesc, setNewChapterDesc] = useState('');

  const reload = async () => {
    try {
      const [d, s, p, a, l, asg] = await Promise.all([
        api(`/teacher/classes/${classId}/detail`),
        api(`/teacher/classes/${classId}/students`),
        api(`/teacher/classes/${classId}/pending-enrollments`),
        api(`/teacher/classes/${classId}/available-students`),
        api(`/teacher/classes/${classId}/lessons`),
        api(`/teacher/assignments?class_id=${classId}`),
      ]);
      if (d?.id) setDetail(d);
      if (s?.students) setStudents(s.students);
      if (p?.enrollments) setPending(p.enrollments);
      if (a?.students) setAvailable(a.students);
      if (l?.lessons) {
        setChapters(l.lessons);
        setSelectedChapterId((prev) => prev || (l.lessons[0]?.id ?? null));
      }
      if (asg?.assignments) setAssignments(asg.assignments);
    } catch { /* silent */ }
    setInitialLoading(false);
  };

  const loadTopics = async (lessonId, { force = false } = {}) => {
    if (!lessonId) return;
    if (!force && topicsByChapter[lessonId]) return;
    setLoadingTopics(true);
    try {
      const r = await api(`/teacher/lessons/${lessonId}/topics`);
      if (r.topics) setTopicsByChapter(prev => ({ ...prev, [lessonId]: r.topics }));
    } catch { /* silent */ }
    setLoadingTopics(false);
  };

  useEffect(() => { if (classId) reload(); /* eslint-disable-next-line */ }, [classId]);
  useEffect(() => { if (selectedChapterId) loadTopics(selectedChapterId); /* eslint-disable-next-line */ }, [selectedChapterId]);

  const handleAction = async (enrollment_id, action) => {
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/enrollment-action`, {
      method: 'POST', body: JSON.stringify({ enrollment_id, action }),
    });
    if (r.message) toast.success(r.message);
    else notifyError(r, 'Failed');
    await reload();
    onStudentsChanged && onStudentsChanged();
    setBusy(false);
  };

  const handleAddOne = async (email) => {
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/add-students`, {
      method: 'POST', body: JSON.stringify({ emails: [email] }),
    });
    if (r.added?.length) toast.success(`Added ${r.added[0].name || email}`);
    else if (r.already_enrolled?.length) toast.error('Already enrolled');
    else notifyError(r, 'Failed');
    await reload();
    onStudentsChanged && onStudentsChanged();
    setBusy(false);
  };

  const handleRemove = async (student_id) => {
    if (!(await confirmDialog({ title: 'Remove student', message: 'Remove this student from the class?', confirmLabel: 'Remove', danger: true }))) return;
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/remove-student`, {
      method: 'POST', body: JSON.stringify({ student_id }),
    });
    if (r.message) toast.success(r.message);
    else notifyError(r, 'Failed');
    await reload();
    onStudentsChanged && onStudentsChanged();
    setBusy(false);
  };

  const handleCreateChapter = async () => {
    const title = (newChapterTitle || '').trim();
    if (!title) { toast.error('Chapter title required'); return; }
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/lessons`, {
      method: 'POST',
      body: JSON.stringify({ title, description: newChapterDesc.trim() }),
    });
    if (r.lesson) {
      toast.success(`Chapter "${title}" created`);
      setNewChapterTitle(''); setNewChapterDesc(''); setShowNewChapter(false);
      setSelectedChapterId(r.lesson.id);
      await reload();
    } else {
      notifyError(r, 'Failed');
    }
    setBusy(false);
  };

  const handleDeleteChapter = async (lesson_id, title) => {
    if (!(await confirmDialog({ title: 'Delete chapter', message: `Delete chapter "${title}"? All topics inside will also be removed.`, confirmLabel: 'Delete', danger: true }))) return;
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/lessons/${lesson_id}`, { method: 'DELETE' });
    if (r.removed) {
      toast.success('Chapter removed');
      setTopicsByChapter(prev => { const c = { ...prev }; delete c[lesson_id]; return c; });
      if (selectedChapterId === lesson_id) setSelectedChapterId(null);
    } else notifyError(r, 'Failed');
    await reload();
    setBusy(false);
  };

  const beginEditChapter = (ch) => {
    setEditingChapterId(ch.id);
    setEditTitle(ch.title || '');
    setEditDesc(ch.description || '');
  };

  const handleSaveChapter = async () => {
    const title = (editTitle || '').trim();
    if (!title) { toast.error('Title cannot be empty'); return; }
    setBusy(true);
    const r = await api(`/teacher/classes/${classId}/lessons/${editingChapterId}`, {
      method: 'PUT',
      body: JSON.stringify({ title, description: editDesc.trim() }),
    });
    if (r.lesson) {
      toast.success('Chapter updated');
      setEditingChapterId(null);
      await reload();
    } else notifyError(r, 'Failed');
    setBusy(false);
  };

  const handleDeleteTopic = async (topic) => {
    if (!(await confirmDialog({ title: 'Remove topic', message: `Remove topic "${topic.title}" from this chapter?`, confirmLabel: 'Remove', danger: true }))) return;
    setBusy(true);
    const r = await api(`/teacher/topics/${topic.id}`, { method: 'DELETE' });
    if (r.removed) {
      toast.success('Topic removed');
      await loadTopics(topic.lesson_id, { force: true });
      await reload();
    } else notifyError(r, 'Failed');
    setBusy(false);
  };

  const handleCreateAssignment = async () => {
    const title = (newAssignment.title || '').trim();
    if (!newAssignmentTopicId) { toast.error('Pick a chapter and topic first'); return; }
    if (!title) { toast.error('Assignment title required'); return; }
    if (!newAssignment.dueDate) { toast.error('Due date required'); return; }
    setBusy(true);
    const r = await api('/teacher/assignments', {
      method: 'POST',
      body: JSON.stringify({
        topic_id: newAssignmentTopicId,
        title,
        description: newAssignment.description.trim(),
        due_date: newAssignment.dueDate,
        due_time: newAssignment.dueTime || '23:59',
        points: newAssignment.points,
      }),
    });
    if (r.assignment) {
      toast.success('Assignment created');
      setShowNewAssignment(false);
      setNewAssignmentChapterId(''); setNewAssignmentTopicId('');
      setNewAssignment({ title: '', description: '', dueDate: '', dueTime: '23:59', points: 10 });
      await reload();
    } else {
      notifyError(r, 'Failed');
    }
    setBusy(false);
  };

  const handleDeleteAssignment = async (assignmentId, title) => {
    if (!(await confirmDialog({ title: 'Delete assignment', message: `Delete assignment "${title}"?`, confirmLabel: 'Delete', danger: true }))) return;
    setBusy(true);
    const r = await api(`/teacher/assignments/${assignmentId}`, { method: 'DELETE' });
    if (r.deleted) { toast.success('Assignment deleted'); await reload(); } else notifyError(r, 'Failed');
    setBusy(false);
  };

  const handleViewTopic = async (topic) => {
    // Fetch full topic (with framework) to preview
    const r = await api(`/teacher/topics/${topic.id}`);
    if (r.topic) setViewingTopic(r.topic);
    else notifyError(r, 'Could not load topic');
  };

  const filteredAvailable = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter(s => (s.full_name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q));
  }, [available, search]);

  const selectedChapter = chapters.find(c => c.id === selectedChapterId) || null;
  const selectedTopics = selectedChapterId ? (topicsByChapter[selectedChapterId] || []) : [];
  const filteredChapters = useMemo(() => {
    const q = chapterSearch.trim().toLowerCase(); if (!q) return chapters;
    return chapters.filter(c => (c.title || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q));
  }, [chapters, chapterSearch]);
  const filteredTopics = useMemo(() => {
    const q = topicSearch.trim().toLowerCase(); if (!q) return selectedTopics;
    return selectedTopics.filter(t => (t.title || '').toLowerCase().includes(q) || (t.summary || '').toLowerCase().includes(q) || (t.key_topics || []).some(kt => (kt || '').toLowerCase().includes(q)));
  }, [selectedTopics, topicSearch]);

  if (!classId) return null;
  return (
    <div className="fixed inset-0 z-[75] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 md:p-6" onClick={onClose} data-testid="class-detail-drawer">
      <div
        className="w-full max-w-[1320px] h-[96vh] md:h-[92vh] bg-white rounded-2xl md:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="relative px-6 md:px-8 py-5 border-b border-zinc-100 bg-gradient-to-br from-[#34A853]/8 via-white to-[#06B6D4]/6 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {detail ? (
                <>
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#34A853] mb-1">
                    {(detail.subjects || []).join(' · ') || 'Class'}
                  </p>
                  <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 truncate tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {detail.name}
                  </h2>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {detail.class_code && <span className="text-[11px] font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">{detail.class_code}</span>}
                    {detail.department?.name && <span className="text-[11px] text-gray-600">{detail.department.name}</span>}
                    {detail.batch?.name && <span className="text-[11px] text-gray-400">· {detail.batch.name}</span>}
                    <span className="text-[11px] text-gray-400">·</span>
                    <span className="text-[11px] text-gray-500 flex items-center gap-1"><Users className="w-3 h-3" /> {detail.student_count} students</span>
                    {detail.pending_count > 0 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{detail.pending_count} pending</span>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-[13px] text-gray-400">Loading…</p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 flex-shrink-0" data-testid="drawer-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4 -mb-5 overflow-x-auto">
            {[
              { id: 'lessons', label: 'Chapters', icon: BookOpen, badge: chapters.length },
              { id: 'info', label: 'Info', icon: FileText },
              { id: 'students', label: 'Students', icon: Users, badge: students.length },
              { id: 'pending', label: 'Pending', icon: CheckCircle2, badge: pending.length, highlight: pending.length > 0 },
              { id: 'assignments', label: 'Assignments', icon: ClipboardList, badge: assignments.length },
              { id: 'insights', label: 'ML Insights', icon: Activity },
              detail?.is_class_teacher ? { id: 'add', label: 'Add Student', icon: Plus } : null,
            ].filter(Boolean).map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 text-[12px] font-semibold px-3 py-3 border-b-2 transition-all whitespace-nowrap ${tab === t.id ? 'text-[#34A853] border-[#34A853]' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
                data-testid={`drawer-tab-${t.id}`}>
                <t.icon className="w-3.5 h-3.5" />{t.label}
                {typeof t.badge === 'number' && (
                  <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${t.highlight ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'}`}>{t.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-hidden bg-gray-50/40">
          {initialLoading && <p className="text-[13px] text-gray-400 text-center py-10">Loading…</p>}

          {!initialLoading && tab === 'lessons' && (
            <div className="h-full flex" data-testid="drawer-lessons">
              {/* Left rail — chapters list */}
              <div className="w-[300px] border-r border-zinc-100 bg-white flex flex-col overflow-hidden flex-shrink-0">
                <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-700">Chapters</p>
                    <p className="text-[10px] text-gray-400">{chapters.length} total{chapterSearch && filteredChapters.length !== chapters.length ? ` · ${filteredChapters.length} shown` : ''}</p>
                  </div>
                  <button
                    onClick={() => setShowNewChapter(v => !v)}
                    className="text-[11px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] px-2.5 py-1.5 rounded-md flex items-center gap-1"
                    data-testid="chapter-new-btn"
                  >
                    <Plus className="w-3 h-3" /> New
                  </button>
                </div>

                <div className="px-3 py-2 border-b border-zinc-100 bg-gray-50/60">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      value={chapterSearch}
                      onChange={e => setChapterSearch(e.target.value)}
                      placeholder="Search chapters…"
                      className="w-full h-8 pl-8 pr-2 rounded-md border border-zinc-200 bg-white text-[11px] focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
                      data-testid="chapter-search-input"
                    />
                    {chapterSearch && (
                      <button onClick={() => setChapterSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"><X className="w-3 h-3" /></button>
                    )}
                  </div>
                </div>

                {showNewChapter && (
                  <div className="p-3 border-b border-zinc-100 bg-[#34A853]/5" data-testid="create-chapter-form">
                    <input
                      value={newChapterTitle}
                      onChange={(e) => setNewChapterTitle(e.target.value)}
                      placeholder="Chapter name, e.g. Newton's Laws"
                      className="w-full h-9 px-2.5 rounded-md border border-zinc-200 text-[12px] focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 mb-2 bg-white"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreateChapter(); }}
                      data-testid="create-chapter-title"
                      autoFocus
                    />
                    <textarea
                      value={newChapterDesc}
                      onChange={(e) => setNewChapterDesc(e.target.value)}
                      placeholder="Short description (optional)"
                      rows={2}
                      className="w-full px-2.5 py-1.5 rounded-md border border-zinc-200 text-[11px] focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 mb-2 bg-white resize-none"
                      data-testid="create-chapter-desc"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCreateChapter}
                        disabled={busy || !newChapterTitle.trim()}
                        className="flex-1 h-8 text-[11px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] disabled:opacity-40 rounded-md"
                        data-testid="create-chapter-submit"
                      >Create chapter</button>
                      <button
                        onClick={() => { setShowNewChapter(false); setNewChapterTitle(''); setNewChapterDesc(''); }}
                        className="h-8 text-[11px] font-semibold text-gray-500 hover:text-gray-800 px-2"
                      >Cancel</button>
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto p-2">
                  {chapters.length === 0 && !showNewChapter && (
                    <div className="p-6 text-center">
                      <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-[12px] text-gray-500">No chapters yet.</p>
                      <p className="text-[11px] text-gray-400 mt-1">Create one to organise daily topics.</p>
                    </div>
                  )}
                  {chapters.length > 0 && filteredChapters.length === 0 && (
                    <div className="p-6 text-center">
                      <Search className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                      <p className="text-[11px] text-gray-500">No chapters match "{chapterSearch}"</p>
                    </div>
                  )}
                  {filteredChapters.map((ch) => {
                    const active = selectedChapterId === ch.id;
                    return (
                      <button
                        key={ch.id}
                        onClick={() => setSelectedChapterId(ch.id)}
                        className={`w-full text-left rounded-lg p-2.5 mb-1 border transition-all ${active ? 'bg-[#34A853]/8 border-[#34A853]/30 shadow-sm' : 'border-transparent hover:bg-gray-50 hover:border-zinc-200'}`}
                        data-testid={`chapter-item-${ch.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#34A853] text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                            <BookOpen className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-[12px] font-semibold truncate ${active ? 'text-[#0d9488]' : 'text-gray-800'}`}>{ch.title}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">{ch.topic_count || 0} topic{(ch.topic_count || 0) !== 1 ? 's' : ''}{ch.latest_topic_date && ` · ${new Date(ch.latest_topic_date).toLocaleDateString()}`}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Main pane — chapter detail + topic timeline */}
              <div className="flex-1 overflow-y-auto">
                {!selectedChapter ? (
                  <div className="h-full flex items-center justify-center p-10">
                    <div className="text-center max-w-sm">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#34A853]/15 to-[#06B6D4]/15 flex items-center justify-center mx-auto mb-3">
                        <Layers className="w-7 h-7 text-[#0d9488]" />
                      </div>
                      <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pick a chapter</h3>
                      <p className="text-[12px] text-gray-500 mt-1">Select a chapter on the left to see every day's published topic inside it.</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 md:p-8 space-y-5">
                    {/* Chapter header card */}
                    <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                      {editingChapterId === selectedChapter.id ? (
                        <div data-testid="edit-chapter-form">
                          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#34A853] mb-2">Edit chapter</p>
                          <input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-[14px] font-semibold focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 mb-2"
                            placeholder="Chapter title"
                            data-testid="edit-chapter-title"
                            autoFocus
                          />
                          <textarea
                            value={editDesc}
                            onChange={(e) => setEditDesc(e.target.value)}
                            rows={2}
                            placeholder="Short description (optional)"
                            className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-[12px] focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 mb-3 resize-none"
                            data-testid="edit-chapter-desc"
                          />
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleSaveChapter}
                              disabled={busy || !editTitle.trim()}
                              className="text-[12px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] disabled:opacity-40 px-4 py-2 rounded-lg"
                              data-testid="edit-chapter-save"
                            >Save</button>
                            <button
                              onClick={() => setEditingChapterId(null)}
                              className="text-[12px] font-semibold text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100"
                              data-testid="edit-chapter-cancel"
                            >Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#34A853] mb-1">Chapter</p>
                            <h3 className="text-xl font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedChapter.title}</h3>
                            {selectedChapter.description && <p className="text-[12px] text-gray-600 mt-1 leading-relaxed">{selectedChapter.description}</p>}
                            <div className="flex items-center gap-3 mt-3 text-[11px] text-gray-500">
                              <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {selectedTopics.length} topic{selectedTopics.length !== 1 ? 's' : ''}</span>
                              {selectedChapter.created_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Created {new Date(selectedChapter.created_at).toLocaleDateString()}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => beginEditChapter(selectedChapter)}
                              disabled={busy}
                              className="text-gray-500 hover:text-[#34A853] p-2 rounded-lg hover:bg-[#34A853]/10 disabled:opacity-40"
                              title="Edit chapter"
                              data-testid={`chapter-edit-${selectedChapter.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteChapter(selectedChapter.id, selectedChapter.title)}
                              disabled={busy}
                              className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 disabled:opacity-40"
                              title="Delete chapter"
                              data-testid={`chapter-delete-${selectedChapter.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Topics timeline */}
                    <div>
                      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Topics — date-wise</p>
                        <div className="relative flex-1 min-w-[220px] max-w-[340px]">
                          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            value={topicSearch}
                            onChange={e => setTopicSearch(e.target.value)}
                            placeholder="Search topics or lectures…"
                            className="w-full h-8 pl-8 pr-8 rounded-md border border-zinc-200 bg-white text-[11px] focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
                            data-testid="topic-search-input"
                          />
                          {topicSearch && (
                            <button onClick={() => setTopicSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"><X className="w-3 h-3" /></button>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400">Publish from Content Studio → routes here automatically</p>
                      </div>

                      {loadingTopics && <p className="text-[12px] text-gray-400 text-center py-6">Loading topics…</p>}

                      {!loadingTopics && selectedTopics.length === 0 && (
                        <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-10 text-center">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#34A853]/15 to-[#0d9488]/15 flex items-center justify-center mx-auto mb-3">
                            <Sparkles className="w-5 h-5 text-[#0d9488]" />
                          </div>
                          <p className="text-[13px] font-semibold text-gray-800">No topics here yet</p>
                          <p className="text-[11px] text-gray-500 mt-1 max-w-sm mx-auto">Head over to <span className="font-semibold">Content Studio</span>, edit a Deskey Session, and publish — pick this chapter and the topic will land right here, dated.</p>
                        </div>
                      )}

                      {!loadingTopics && selectedTopics.length > 0 && filteredTopics.length === 0 && (
                        <div className="rounded-xl border border-dashed border-zinc-200 bg-white p-6 text-center">
                          <p className="text-[12px] text-gray-500">No topics match "{topicSearch}"</p>
                        </div>
                      )}

                      {!loadingTopics && filteredTopics.length > 0 && (
                        <div className="space-y-2">
                          {filteredTopics.map((topic) => (
                            <TopicRow
                              key={topic.id}
                              topic={topic}
                              onView={() => handleViewTopic(topic)}
                              onEdit={() => {
                                if (!topic.draft_id) { toast.error('No Deskey Session draft linked to this topic'); return; }
                                navigate(`/teacher?draft=${topic.draft_id}`);
                              }}
                              onDelete={() => handleDeleteTopic(topic)}
                              busy={busy}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {!initialLoading && tab === 'info' && detail && (
            <div className="p-6 md:p-8 space-y-4 overflow-y-auto h-full" data-testid="drawer-info">
              <div className="grid grid-cols-3 gap-3">
                <StatBox label="Students" value={detail.student_count} sub={`of ${detail.capacity || '∞'}`} />
                <StatBox label="Pending" value={detail.pending_count} accent="#f59e0b" />
                <StatBox label="Chapters" value={chapters.length} accent="#8B5CF6" />
              </div>
              <div className="rounded-xl bg-white border border-zinc-100 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Subjects you teach</p>
                <div className="flex gap-1.5 flex-wrap">
                  {(detail.subjects || []).map(s => (
                    <span key={s} className="text-[11px] font-semibold bg-[#34A853]/10 text-[#34A853] px-2.5 py-1 rounded-full">{s}</span>
                  ))}
                  {(!detail.subjects || detail.subjects.length === 0) && <span className="text-[11px] text-gray-400">No subjects scheduled yet</span>}
                </div>
              </div>
              {detail.schedule && detail.schedule.length > 0 && (
                <div className="rounded-xl bg-white border border-zinc-100 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Your upcoming slots</p>
                  <div className="space-y-1.5">
                    {detail.schedule.slice(0, 6).map((row, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-500">{row.day_of_week || ''}</span>
                        <span className="text-gray-700">{(row.slots || []).map(s => `${s.start_time}–${s.end_time} ${s.subject || ''}`).join(', ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!initialLoading && tab === 'students' && (
            <div className="p-6 md:p-8 space-y-2 overflow-y-auto h-full" data-testid="drawer-students">
              {students.length === 0 && <p className="text-[12px] text-gray-400 text-center py-6">No students enrolled yet.</p>}
              {students.map(s => (
                <StudentRow key={s.id} name={s.student_name} email={s.student_email} onRemove={() => handleRemove(s.student_id)} disabled={busy} testid={`student-${s.student_id}`} />
              ))}
            </div>
          )}

          {!initialLoading && tab === 'pending' && (
            <div className="p-6 md:p-8 space-y-2 overflow-y-auto h-full" data-testid="drawer-pending">
              {pending.length === 0 && <p className="text-[12px] text-gray-400 text-center py-6">No pending requests.</p>}
              {pending.map(s => (
                <div key={s.id} className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50/40 p-3" data-testid={`pending-${s.id}`}>
                  <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center flex-shrink-0">
                    {(s.student_name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800 truncate">{s.student_name}</p>
                    <p className="text-[10px] text-gray-500 truncate">{s.student_email}</p>
                  </div>
                  <button onClick={() => handleAction(s.id, 'approve')} disabled={busy} className="text-[11px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] px-3 py-1.5 rounded-lg disabled:opacity-40" data-testid={`approve-${s.id}`}>Approve</button>
                  <button onClick={() => handleAction(s.id, 'reject')} disabled={busy} className="text-[11px] font-semibold text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-lg disabled:opacity-40" data-testid={`reject-${s.id}`}>Reject</button>
                </div>
              ))}
            </div>
          )}

          {!initialLoading && tab === 'assignments' && (
            <div className="p-6 md:p-8 space-y-3 overflow-y-auto h-full" data-testid="drawer-assignments">
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-gray-500">{assignments.length} assignment{assignments.length !== 1 ? 's' : ''} for this class</p>
                <button
                  onClick={() => setShowNewAssignment(v => !v)}
                  className="text-[11px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] px-2.5 py-1.5 rounded-md flex items-center gap-1"
                  data-testid="assignment-new-btn"
                >
                  <Plus className="w-3 h-3" /> New
                </button>
              </div>

              {showNewAssignment && (
                <div className="rounded-xl border-2 border-[#34A853]/30 bg-white overflow-hidden" data-testid="create-assignment-form">
                  <div className="p-4 pb-0 grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 block">Chapter *</span>
                      <select
                        value={newAssignmentChapterId}
                        onChange={(e) => { const id = e.target.value; setNewAssignmentChapterId(id); setNewAssignmentTopicId(''); if (id) loadTopics(id); }}
                        className="w-full h-10 px-2 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        data-testid="assignment-chapter-select"
                      >
                        <option value="">Select chapter…</option>
                        {chapters.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 block">Topic *</span>
                      <select
                        value={newAssignmentTopicId}
                        onChange={(e) => setNewAssignmentTopicId(e.target.value)}
                        disabled={!newAssignmentChapterId}
                        className="w-full h-10 px-2 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
                        data-testid="assignment-topic-select"
                      >
                        <option value="">{newAssignmentChapterId ? 'Select topic…' : 'Pick a chapter first'}</option>
                        {(topicsByChapter[newAssignmentChapterId] || []).map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="p-4">
                    <AssignmentBlock
                      enabled
                      hideToggle
                      title={newAssignment.title}
                      description={newAssignment.description}
                      dueDate={newAssignment.dueDate}
                      dueTime={newAssignment.dueTime}
                      points={newAssignment.points}
                      onChange={(patch) => setNewAssignment(prev => ({ ...prev, ...patch }))}
                    />
                  </div>
                  <div className="px-4 pb-4 flex items-center justify-end gap-2">
                    <button onClick={() => setShowNewAssignment(false)} className="text-[11px] font-semibold text-gray-500 hover:text-gray-800 px-3 py-2 rounded-lg">Cancel</button>
                    <button onClick={handleCreateAssignment} disabled={busy} className="text-[11px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] px-4 py-2 rounded-lg disabled:opacity-40" data-testid="assignment-create-submit">Create assignment</button>
                  </div>
                </div>
              )}

              {assignments.length === 0 && !showNewAssignment && (
                <p className="text-[12px] text-gray-400 text-center py-6">No assignments yet for this class.</p>
              )}
              <div className="space-y-1.5">
                {assignments.map(a => (
                  <div key={a.id} className="flex items-center gap-3 rounded-xl border border-zinc-100 bg-white p-3" data-testid={`assignment-${a.id}`}>
                    <div className="w-9 h-9 rounded-lg bg-[#34A853]/10 text-[#34A853] flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-gray-800 truncate">{a.title}</p>
                      <p className="text-[10px] text-gray-500 truncate flex items-center gap-1">
                        <CalendarDays className="w-2.5 h-2.5" /> Due {a.due_date}{a.due_time ? ` · ${a.due_time}` : ''} · {a.points} pts
                      </p>
                    </div>
                    <button onClick={() => handleDeleteAssignment(a.id, a.title)} disabled={busy} className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 disabled:opacity-40" data-testid={`assignment-delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!initialLoading && tab === 'insights' && (
            <div className="p-5 md:p-7 overflow-y-auto h-full" data-testid="drawer-insights">
              <ClassInsightsPanel classId={classId} />
            </div>
          )}

          {!initialLoading && tab === 'add' && detail?.is_class_teacher && (
            <div className="p-6 md:p-8 space-y-3 overflow-y-auto h-full" data-testid="drawer-add">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="w-full h-10 pl-10 pr-3 rounded-lg border border-zinc-200 text-[12px] focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40"
                  data-testid="add-student-search"
                />
              </div>
              {filteredAvailable.length === 0 && (
                <p className="text-[12px] text-gray-400 text-center py-6">
                  {available.length === 0 ? 'All students are already in this class.' : 'No matches.'}
                </p>
              )}
              <div className="space-y-1.5">
                {filteredAvailable.slice(0, 50).map(s => (
                  <div key={s.id} className="flex items-center gap-3 rounded-xl border border-zinc-100 bg-white p-3 hover:border-[#34A853]/30 transition-colors" data-testid={`available-${s.id}`}>
                    <div className="w-9 h-9 rounded-full bg-zinc-100 text-zinc-600 font-bold flex items-center justify-center flex-shrink-0">
                      {(s.full_name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-gray-800 truncate">{s.full_name}</p>
                      <p className="text-[10px] text-gray-500 truncate">{s.email}</p>
                    </div>
                    <button onClick={() => handleAddOne(s.email)} disabled={busy} className="text-[11px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] px-3 py-1.5 rounded-lg disabled:opacity-40" data-testid={`add-${s.id}`}>Add</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Topic preview — reuses the same SessionPreview the Content Editor
          uses, so a published topic renders exactly as students see it
          instead of a bespoke, drift-prone renderer. SessionPreview itself
          is `fixed inset-0` (it's meant to take over the whole screen when
          opened from the Content Editor's own full page). Nested here inside
          the My Classes popup, that would visually read as a full-page
          redirect instead of a preview INSIDE the popup — so we wrap it in a
          bounded, centered card and give that card `transform`, which makes
          it a new containing block for `position: fixed` descendants. That
          confines SessionPreview's "fixed inset-0" to the card's box instead
          of the real viewport, without needing to change SessionPreview
          itself (the Content Editor's own use of it still wants full-screen). */}
      {viewingTopic && (
        <div
          className="absolute inset-0 z-[80] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setViewingTopic(null)}
          data-testid="topic-preview"
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[85%] overflow-hidden"
            style={{ transform: 'translateZ(0)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <SessionPreview
              draft={viewingTopic}
              framework={viewingTopic.framework}
              sections={viewingTopic.framework?.sections}
              references={viewingTopic.references || []}
              onClose={() => setViewingTopic(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Topic row in timeline ────────────────────────────────────────────────────
function TopicRow({ topic, onView, onEdit, onDelete, busy }) {
  const date = topic.topic_date ? new Date(topic.topic_date) : null;
  const subtitle = (topic.summary || '').trim().slice(0, 160);
  return (
    <div className="flex items-stretch gap-3 rounded-xl border border-zinc-200 bg-white p-3.5 hover:border-[#34A853]/40 hover:shadow-sm transition-all group" data-testid={`topic-${topic.id}`}>
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#34A853]/10 to-[#06B6D4]/10 border border-[#34A853]/20 flex flex-col items-center justify-center flex-shrink-0">
        <span className="text-[9px] font-bold uppercase tracking-wider text-[#0d9488]">{date ? date.toLocaleString('en-US', { month: 'short' }) : '—'}</span>
        <span className="text-[18px] font-extrabold text-[#0d9488] leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>{date ? date.getDate() : '--'}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <p className="text-[14px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{topic.title}</p>
          <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Published</span>
        </div>
        {subtitle && <p className="text-[11px] text-gray-600 line-clamp-2 leading-relaxed">{subtitle}</p>}
        {(topic.key_topics || []).length > 0 && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {(topic.key_topics || []).slice(0, 4).map((t, i) => (
              <span key={i} className="text-[10px] font-semibold bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{t}</span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col justify-between items-end gap-1">
        <div className="flex items-center gap-1">
          <button
            onClick={onView}
            className="text-[11px] font-semibold text-[#0d9488] hover:text-white hover:bg-[#34A853] border border-[#34A853]/30 hover:border-[#34A853] px-2.5 py-1 rounded-md flex items-center gap-1 transition-all"
            data-testid={`topic-view-${topic.id}`}
          >
            <Eye className="w-3 h-3" /> View
          </button>
          <button
            onClick={onEdit}
            className="text-[11px] font-semibold text-gray-700 hover:text-[#0d9488] border border-zinc-200 hover:border-[#34A853]/40 hover:bg-[#34A853]/5 px-2.5 py-1 rounded-md flex items-center gap-1 transition-all"
            title="Open in Content Editor"
            data-testid={`topic-edit-${topic.id}`}
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
        </div>
        <button
          onClick={onDelete}
          disabled={busy}
          className="text-gray-400 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 disabled:opacity-40 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove topic"
          data-testid={`topic-delete-${topic.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Small helpers
function StatBox({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl bg-white border border-zinc-100 p-3 text-center">
      <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className="text-xl font-extrabold mt-0.5" style={{ color: accent || '#111', fontFamily: 'Manrope, sans-serif' }}>{value}</p>
      {sub && <p className="text-[9px] text-gray-400">{sub}</p>}
    </div>
  );
}
function StudentRow({ name, email, onRemove, disabled, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-zinc-100 bg-white p-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-full bg-[#34A853]/10 text-[#34A853] font-bold flex items-center justify-center flex-shrink-0">
        {(name || '?').slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-gray-800 truncate">{name}</p>
        <p className="text-[10px] text-gray-500 truncate flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{email}</p>
      </div>
      <button onClick={onRemove} disabled={disabled} className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 disabled:opacity-40"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );
}

// ── Default export — full "My Classes" page ─────────────────────────────────
export default function TeacherMyClasses({ onQuickPick }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [openClassId, setOpenClassId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api('/teacher/classes/grouped');
      if (res.groups) { setGroups(res.groups); setTotal(res.total_classes || 0); }
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-8" data-testid="teacher-my-classes">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Classes</h1>
        <p className="text-[13px] text-gray-500 mt-1">{total} class{total !== 1 ? 'es' : ''} across your departments &amp; batches.</p>
      </div>
      {loading ? (
        <div className="text-center py-14"><p className="text-[13px] text-gray-400">Loading your classes…</p></div>
      ) : (
        <GroupedClassGrid
          groups={groups}
          onOpenClass={(c) => setOpenClassId(c.id)}
          onQuickPick={onQuickPick}
          emptyMessage="No classes are assigned to you yet. Ask your department admin to add you to a schedule."
        />
      )}
      {openClassId && (
        <ClassDetailDrawer
          classId={openClassId}
          onClose={() => setOpenClassId(null)}
          onStudentsChanged={load}
        />
      )}
    </div>
  );
}
